<template>
  <div v-if="isLoadingAnything" class="mel-page-loader"></div>
  <div ref="wrapperPart" class="wrapper container-fluid d-flex" :class="{'bg-blur': isLoadingAnything}">
    <div ref="sidebarPart" class="sidebar-wrapper d-none d-md-flex d-print-none bg-body-tertiary">
      <NavbarComponent />
    </div>
    <div class="d-flex flex-column h-100 p-0 flex-fill">
      <div class="d-flex d-md-none d-print-none">
        <SymbolbarComponent />
      </div>
      <div ref="contentPart" class="flex-fill flex-grow-1">
        <RouterView />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterView } from 'vue-router';
import { NavbarComponent, SymbolbarComponent } from '@/components/navigation';
import { router } from './services';
import emailService from './services/EmailService';
import mainHub from './hubs/mainHub';
import { throttledLoadOrganization, throttledLoadTenantMasterdata, throttledLoadSiteMasterdata, throttledLoadIdentiy, loadOrganization, isLoadingOrganization, isLoadingIdentity, isLoadingMasterdata } from '@/services/storeLoader';
import { tryRefreshToken } from "@/services/api";
import { useAuthStore, useSessionStore, useBuildStore } from "@/stores";
import { initColorMode } from '@/services/colorMode';
import { storeToRefs } from 'pinia';
import i18n from './i18n';
import { toast } from 'vue3-toastify';

const FORCED_RESET_VIRTUAL_SHA = 'forced-reset-virtual-sha';
const contentPart = ref();
const wrapperPart = ref();
const sidebarPart = ref();
const isLoadingAnything = computed(() => {
  return isLoadingOrganization.value || isLoadingIdentity.value || isLoadingMasterdata.value;
});

let frontendServerId = '';
let implicitLoginOnce = false;
let currentSha: string | null = null;
let reloadScheduled = false;
let pollTimer: number | null = null;

function scheduleReload(reason: string, delayMs = 3_000) {
  if (reloadScheduled) return;
  reloadScheduled = true;
  toast.dark(i18n.global.t('action.triggeredPageReload', { reason }), {
    autoClose: delayMs,
    closeOnClick: true,
    pauseOnFocusLoss: false,
    pauseOnHover: false,
    transition: 'slide',
    onClick: () => {
      window.location.reload();
    },
    position: 'bottom-center'
  });
  nextTick(() => {
    // Blur background after toast is shown
    if (wrapperPart.value) {
      wrapperPart.value.classList.add('bg-blur');
    }
  });
  // Fallback auto-reload if user doesn't click
  window.setTimeout(() => window.location.reload(), delayMs);
}

// Compare builds and prompt reload if changed
async function checkForNewVersion() {
  try {
    const buildStore = useBuildStore();
    await buildStore.fetchAll(true);
    const sha = buildStore.getByKey('Sha');
    if (!currentSha) {
      currentSha = sha || null;
      return;
    }
    if (sha && currentSha && sha !== currentSha) {
      scheduleReload(currentSha === FORCED_RESET_VIRTUAL_SHA ? i18n.global.t('reason.reloadPageForcedByServer'): i18n.global.t('reason.reloadPageNewServerBuild'));
    }
  } catch {
    // ignore
  }
}

router.beforeEach(async (to) => {
  // redirect to login page if not logged in and trying to access a restricted page
  const requiresAuth = !to.meta.anonymousAccess;
  const authStore = useAuthStore();
  const sessionStore = useSessionStore();

  const { selectedSiteId } = storeToRefs(sessionStore);
  const { isAuthenticated, returnUrl } = storeToRefs(authStore);

  if (requiresAuth && !implicitLoginOnce) {
    // console.debug('Implicit login required', to.name, to.params);
    await authStore.implicitLogin();
    if (isAuthenticated) implicitLoginOnce = true;
  }
  
  if (requiresAuth && !isAuthenticated.value) {
    returnUrl.value = to.fullPath;
    return { name: "login", params: to.params };
  }

  if (!isAuthenticated.value) {
    return
  }

  await loadOrganization();

  // redirect to site view if logged in and site not set
  if (!to.meta.sitelessPage && selectedSiteId.value <= 0) {
    return { name: "site", params: { locale: i18n.global.locale.value, ...to.params } };
  }

  if (to.name === undefined || to.name === "login" && selectedSiteId.value > 0) {
    return { name: "home", params: { locale: i18n.global.locale.value, ...to.params } };
  }
});

router.afterEach((to) => {
  if (to.meta.useTenantBgImg) {
    if (contentPart.value) {
      contentPart.value.classList.add('tenant-bg-img');
    }
  }
  else if (to.meta.useTenantBg) {
    if (contentPart.value) {
      contentPart.value.classList.add('tenant-bg');
    }
  }
  else {
    if (contentPart.value) {
      contentPart.value.classList.remove('tenant-bg');
      contentPart.value.classList.remove('tenant-bg-img');
    }
  }

  if (to.meta.hideSidebar) {
    if (wrapperPart.value && sidebarPart.value) {
      wrapperPart.value.classList.add('p-0');
      wrapperPart.value.classList.remove('wrapper');
      sidebarPart.value.classList.remove('sidebar-wrapper');
      sidebarPart.value.classList.remove('d-md-flex');
    }
  }
  else {
    if (wrapperPart.value && sidebarPart.value) {
      wrapperPart.value.classList.remove('p-0');
      wrapperPart.value.classList.add('wrapper');
      sidebarPart.value.classList.add('sidebar-wrapper');
      sidebarPart.value.classList.add('d-md-flex');
    }
  }
});

onBeforeMount(() => {
  const localStorageVer = Number.parseInt(localStorage.getItem('ver') ?? '0');
  if (localStorageVer < 1) {
    console.info('Local storage version is too old, clearing local storage');
    localStorage.clear();
    localStorage.setItem('ver', '1');
  }
  initColorMode();
});

onMounted(async () => {
  const authStore = useAuthStore();
  const sessionStore = useSessionStore();
  const buildStore = useBuildStore();

  const { isAuthenticated } = storeToRefs(authStore);
  const { selectedTenantId, selectedSiteId } = storeToRefs(sessionStore);

  // Capture current build SHA for comparisons
  try {
    await buildStore.fetchAll(false);
    currentSha = buildStore.getByKey('Sha');
  } catch {
    currentSha = null;
  }

  mainHub.onStaticContentChanged(async () => {
    console.debug('HUB: Static Content Changed');
    // Could reset to null to force reload check
    currentSha = FORCED_RESET_VIRTUAL_SHA; // Set to trigger forced reload
    await checkForNewVersion();
  });
  mainHub.onOrganizationChanged(() => {
    console.debug('HUB: Organization changed');
    throttledLoadOrganization();
  });
  mainHub.onIdentityChanged(() => {
    console.debug('HUB: Identity Changed');
    throttledLoadIdentiy();
  });
  mainHub.onMasterdataChanged((tenantId) => {
    console.debug('HUB: Tenant Masterdata changed');
    if (tenantId) {
      if (tenantId === selectedTenantId.value) {
        console.debug('Tenant Masterdata changed for current tenant');
        throttledLoadTenantMasterdata();
      }
      else {
        console.debug('Tenant Masterdata changed for another tenant');
      }
    }
    else {
      console.debug('Tenant Masterdata changed for all tenants');
      throttledLoadTenantMasterdata();
    }
  });
  mainHub.onSiteMasterdataChanged((siteId) => {
    console.debug('HUB: Site Masterdata Changed');
    if (siteId) {
      if (siteId === selectedSiteId.value) {
        console.debug('Site Masterdata changed for current site');
        throttledLoadSiteMasterdata();
      }
      else {
        console.debug('Site Masterdata changed for another site');
      }
    }
    else {
      console.debug('Site Masterdata changed for all sites');
      throttledLoadSiteMasterdata();
    }
  });
  mainHub.onEmailQueueChanged(async (id) => {
    console.debug('HUB: Email Queue Changed', id);
    emailService.emailChanged(id);
  });
  mainHub.onServerId(async (serverId) => {
    if (frontendServerId === serverId) {
      console.debug('ServerId already set to', serverId);
      return;
    }
    if (frontendServerId == '') {
      frontendServerId = serverId;
      console.debug('ServerId set to', serverId);
      return;
    }
    frontendServerId = serverId;
    console.debug('ServerId changed to', serverId);
    await checkForNewVersion();
  });
  mainHub.start();

  watch(selectedSiteId, (newId, oldId) => {
    if (oldId > 0 && newId !== oldId && router.currentRoute.value.meta.siteSpecificEntity) {
      router.push({ name: 'home', params: { locale: i18n.global.locale.value } });
    }
  });

  // Handle dynamic import chunk errors after a deploy
  const onChunkError = (event: PromiseRejectionEvent) => {
    const msg = String(event?.reason?.message || event?.reason);
    if (msg?.includes('Loading chunk') || msg?.includes('Failed to fetch dynamically imported module') || msg?.includes('Importing a module script failed')) {
      scheduleReload(i18n.global.t('reason.reloadPageAssetChanged'));
    }
  };
  window.addEventListener('unhandledrejection', onChunkError);

  // Periodic polling and on focus/visibility checks
  pollTimer = window.setInterval(() => { checkForNewVersion(); }, 5 * 60 * 1000);
  const onFocus = () => checkForNewVersion();
  window.addEventListener('visibilitychange', onFocus);
  window.addEventListener('focus', onFocus);

  window.setInterval(async () => {
    console.debug('checking session...');
    if (!isAuthenticated.value) {
      console.debug('session not authenticated');
      return;
    }
    const expiresAt = localStorage.getItem('access-token-expiry');
    if (expiresAt && Date.now() > Number(expiresAt) - 10 * 60 * 1000) { // Refresh 10 minutes before expiry
      console.debug('refreshing token...');
      await tryRefreshToken();
    }
  }, 60 * 1000); // Check every minute

  // Cleanup on unmount
  onUnmounted(() => {
    window.removeEventListener('unhandledrejection', onChunkError);
    window.removeEventListener('visibilitychange', onFocus);
    window.removeEventListener('focus', onFocus);
    if (pollTimer) window.clearInterval(pollTimer);
  });
});
</script>

<style type="scss" scoped>
.wrapper {
  transition: all 0.4s ease 0s;
  padding-left: 0;
  padding-right: 0;
}

.sidebar-wrapper {
  transition: all 0.4s ease 0s;
}

/* bootstrap breakpoint md */
@media (min-width: 768px) {
  .wrapper {
    padding-left: 280px;
  }

  .sidebar-wrapper {
    margin-left: -280px;
    left: 280px;
    width: 280px;
    position: fixed;
    height: 100%;
    overflow-y: auto;
    z-index: 1000;
  }
}

.tenant-bg {
  background:
    linear-gradient(-30deg, hsla(64, 35%, 45%, 0.5), hsla(212, 40%, 39%, 0.5))
}

.tenant-bg-img {
  background:
    url('/icon.svg') center / cover no-repeat fixed,
    linear-gradient(-30deg, hsla(64, 35%, 45%, 0.5), hsla(212, 40%, 39%, 0.5))
}

.tenant-bg-img-rega {
  background-image: url('/rega-bg.jpg');
  background-repeat: no-repeat;
  background-size: cover;
}

.bg-blur {
    filter: blur(2px) grayscale(1);
    transition: filter 2s ease;
}

.mel-page-loader {
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 3;

    &:after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border-radius: 10px;
        background: linear-gradient(60deg, var(--bs-primary), var(--bs-melodi));
        background-size: 300% 300%;
        clip-path: polygon(0% 100%, 20px 100%, 20px 20px, calc(100% - 20px) 20px, calc(100% - 20px) calc(100% - 20px), 20px calc(100% - 20px), 20px 100%, 100% 100%, 100% 0%, 0% 0%);
        animation: mel-clip-path-border-20px 1s forwards linear reverse, mel-bg-rotate-80percent 2s linear infinite;
        opacity: 0.8;
        margin: 10px;
    }
}

@keyframes mel-bg-rotate-80percent {
    0% {
        background-position: 0% 0%;
    }

    50% {
        background-position: 0% 100%;
    }

    100% {
        background-position: 0% 0%;
    }
}

@keyframes mel-clip-path-border-20px {
    0% {
        clip-path: polygon(0% 100%, 20px 100%, 20px 20px, calc(100% - 20px) 20px, calc(100% - 20px) calc(100% - 20px), 20px calc(100% - 20px), 20px 100%, 100% 100%, 100% 0%, 0% 0%);
    }

    25% {
        clip-path: polygon(0% 100%, 20px 100%, 20px 20px, calc(100% - 20px) 20px, calc(100% - 20px) calc(100% - 20px), calc(100% - 20px) calc(100% - 20px), calc(100% - 20px) 100%, 100% 100%, 100% 0%, 0% 0%);
    }

    50% {
        clip-path: polygon(0% 100%, 20px 100%, 20px 20px, calc(100% - 20px) 20px, calc(100% - 20px) 20px, calc(100% - 20px) 20px, calc(100% - 20px) 20px, calc(100% - 20px) 20px, 100% 0%, 0% 0%);
    }

    75% {
        -webkit-clip-path: polygon(0% 100%, 20px 100%, 20px 20px, 20px 20px, 20px 20px, 20px 20px, 20px 20px, 20px 20px, 20px 0%, 0% 0%);
    }

    100% {
        -webkit-clip-path: polygon(0% 100%, 20px 100%, 20px 100%, 20px 100%, 20px 100%, 20px 100%, 20px 100%, 20px 100%, 20px 100%, 0% 100%);
    }
}
</style>